import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTelegramPlane, FaYoutube, FaShareAlt, FaGamepad,
  FaCheckCircle, FaClock, FaBolt, FaStar, FaFire, FaTrophy, FaMicrochip
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../context/AppContext";
import NoLoginForTasks from "./NoLoginForTasks";
import TaskCard from "./TaskCard";
import VideoModal from "./vediomadal";
import VoiceTaskSection from "./UserVoiceRecorc";

/* ── Theme ── */
const T = {
  bg: "#050d1a",
  bgCard: "#0a1628",
  bgCard2: "#0d1f38",
  border: "#1a2d4a",
  accent: "#eab308",
  accentDim: "rgba(234,179,8,0.1)",
  accentGlow: "rgba(234,179,8,0.3)",
  blue: "#3b82f6",
  blueDim: "rgba(59,130,246,0.1)",
  textPrimary: "#f0f6ff",
  textSecondary: "#94a3b8",
  textMuted: "#4b6280",
  success: "#22c55e",
  successDim: "rgba(34,197,94,0.1)",
  danger: "#ef4444",
};

/* ── original constants & helpers ── */
const ICON_BY_CATEGORY = {
  watch: FaYoutube,
  subscribe: FaTelegramPlane,
  play: FaGamepad,
  share: FaShareAlt,
};
const TYPE_BY_CATEGORY = {
  watch: "watch",
  subscribe: "share",
  play: "game",
  share: "share",
};
const normalizeId = (v) => (v ? String(v) : "");
const getDayKey = (d = new Date()) => d.toISOString().slice(0, 10);

/* ── Floating dot bg ── */
function FloatDots() {
  const dots = useMemo(() =>
    Array.from({ length: 22 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      size: Math.random() * 2.5 + 0.8,
      delay: Math.random() * 4,
      dur: 4 + Math.random() * 4,
      gold: Math.random() > 0.5,
    })), []);
  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
      {dots.map(d => (
        <motion.div key={d.id}
          animate={{ y: [-8, 8, -8], opacity: [0.15, 0.5, 0.15] }}
          transition={{ duration: d.dur, delay: d.delay, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute", left: d.left, top: d.top,
            width: d.size, height: d.size, borderRadius: "50%",
            background: d.gold ? T.accent : T.blue,
          }} />
      ))}
      <div style={{
        position: "absolute", top: "5%", right: "5%",
        width: "clamp(200px, 50vw, 320px)", height: "clamp(200px, 50vw, 320px)", borderRadius: "50%",
        background: "radial-gradient(ellipse,rgba(234,179,8,0.06) 0%,transparent 70%)",
      }} />
      <div style={{
        position: "absolute", bottom: "10%", left: "5%",
        width: "clamp(150px, 40vw, 260px)", height: "clamp(150px, 40vw, 260px)", borderRadius: "50%",
        background: "radial-gradient(ellipse,rgba(59,130,246,0.07) 0%,transparent 70%)",
      }} />
    </div>
  );
}

/* ── Stat chip ── */
function StatChip({ icon, label, value, color }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 16px",
      background: "rgba(0,0,0,0.25)",
      border: `1px solid ${T.border}`,
      borderRadius: 14,
      flex: "1 1 min(100%, 140px)", minWidth: 0,
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 10,
        background: `${color}18`,
        border: `1px solid ${color}33`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color, flexShrink: 0, fontSize: 13,
      }}>
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 9, fontWeight: 800, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.15em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {label}
        </div>
        <div style={{ fontSize: "clamp(12px, 3vw, 14px)", fontWeight: 900, color: T.textPrimary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {value}
        </div>
      </div>
    </div>
  );
}

/* ── Main ── */
export default function TasksSection() {
  const navigate = useNavigate();
  const [videoUrl, setVideoUrl] = useState(null);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [totalPoints, setTotalPoints] = useState(0);
  const [taskIdsObj, setTaskIdsObj] = useState({});
  const videoTimerRef = useRef(null);
  const [recordedVoice, setRecordedVoice] = useState(null);

  const MAX_POINTS = 100;
  const TASK_POINTS = 50;
  const DAILY_TASKS = 2;
  const syncingRef = useRef(false);
  const [FetchUserTask, setFetchUserTask] = useState([]); 

  const Tasks=[
    {
      id: "tic-tac-toe",
      name:'Tic-Tac-Toe',
      description:"win the Game to claim 50 points",
      points:50,
      category:'game',
      url:'/tic-tac-toe'
    },
    {
      id: "Coin Bubbles ",
      name:'Coin Bubbles ',
      description:"win the Game to claim 50 points",
      points:50,
      category:'game',
      url:'/coin-game'
    },
  ]

  const {
    syncTasksToBackend,
    HandleFetchUserTasks,
    FetchUserData,
    HandleFetchUserData,
    showAlert,
  } = useAppContext();

  const isLoggedIn = !!FetchUserData?._id;
  const isPlanActive = !!FetchUserData?.isActivePlan;
  const todayKey = getDayKey();

  const getLocalDayKey = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  useEffect(() => {
    const tk = getDayKey();
    const savedDate = localStorage.getItem("tasksDate");
    const savedObj = localStorage.getItem("taskIdsObj");
    const savedPoints = localStorage.getItem("totalPoints");
    if (savedDate !== tk) {
      localStorage.setItem("tasksDate", tk);
      localStorage.removeItem("taskIdsObj");
      localStorage.removeItem("totalPoints");
      setTaskIdsObj({});
      setCompletedTasks([]);
      setTotalPoints(0);
      return;
    }
    if (savedObj) {
      try {
        const obj = JSON.parse(savedObj) || {};
        setTaskIdsObj(obj);
        const ids = Object.keys(obj);
        setCompletedTasks(ids.map((id) => ({ id })));
        setTotalPoints(savedPoints ? Number(savedPoints) : Math.min(ids.length * TASK_POINTS, MAX_POINTS));
      } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => { HandleFetchUserData(); }, [HandleFetchUserData]);

  useEffect(() => {
    if (!isLoggedIn || !FetchUserData?._id) return;
    const groups = Array.isArray(FetchUserData?.dailyTasksCompleted) ? FetchUserData.dailyTasksCompleted : [];
    const tg = groups.find((x) => String(x?.date) === String(todayKey));
    if (!tg) return;
    const ids = Array.isArray(tg?.taskIds) ? tg.taskIds.map(normalizeId) : [];
    const obj = {};
    ids.forEach((id) => (obj[id] = true));
    setTaskIdsObj(obj);
    setCompletedTasks(ids.map((id) => ({ id })));
    setTotalPoints(Math.min(ids.length * TASK_POINTS, MAX_POINTS));
    localStorage.setItem("tasksDate", todayKey);
    localStorage.setItem("taskIdsObj", JSON.stringify(obj));
    localStorage.setItem("totalPoints", String(Math.min(ids.length * TASK_POINTS, MAX_POINTS)));
  }, [isLoggedIn, FetchUserData?._id, FetchUserData?.dailyTasksCompleted, todayKey]);

  const isTaskCompleted = (taskId) => !!taskIdsObj[normalizeId(taskId)];

  const dailyTasks = useMemo(() => {
    const tasks = Array.isArray(Tasks) ? Tasks : [];
    return tasks.slice(0, DAILY_TASKS).map((t) => {
      const category = (t.category || "").toLowerCase();
      const id = normalizeId(t._id || t.id);
      return {
        ...t,
        id,
        title: t.name || "Operation Task",
        reward: t.points ?? TASK_POINTS,
        type: TYPE_BY_CATEGORY[category] || "share",
        icon: ICON_BY_CATEGORY[category] || FaShareAlt,
        status: isTaskCompleted(id) ? "completed" : "pending",
      };
    });
  }, [Tasks, taskIdsObj]);

  if (!isLoggedIn) return <NoLoginForTasks />;

  const handleTaskComplete = async (task) => {
    const id = normalizeId(task.id);
    if (isTaskCompleted(id)) return;
    const newObj = { ...taskIdsObj, [id]: true };
    const ids = Object.keys(newObj);
    setTaskIdsObj(newObj);
    setCompletedTasks(ids.map((x) => ({ id: x })));
    setTotalPoints(Math.min(ids.length * TASK_POINTS, MAX_POINTS));
    localStorage.setItem("tasksDate", getDayKey());
    localStorage.setItem("taskIdsObj", JSON.stringify(newObj));
    localStorage.setItem("totalPoints", String(Math.min(ids.length * TASK_POINTS, MAX_POINTS)));
    if (ids.length === 2 && !syncingRef.current) {
      syncingRef.current = true;
      try {
        const ok = await syncTasksToBackend(ids);
        if (ok) {
          localStorage.removeItem("taskIdsObj");
          localStorage.removeItem("totalPoints");
          await HandleFetchUserData();
        }
      } finally { syncingRef.current = false; }
    }
  };

  const handleStartTask = (task) => {
    if (!isPlanActive) {
      showAlert("error", "Plan activation required.");
      navigate("/plans");
      return;
    }
    if (isTaskCompleted(task.id)) return;
    if (task.type === "watch") {
      setVideoUrl(task.url);
      if (videoTimerRef.current) clearTimeout(videoTimerRef.current);
      videoTimerRef.current = setTimeout(() => {
        handleTaskComplete(task);
        setVideoUrl(null);
      }, 10000);
    } else if (task.type === "game") {
      navigate(task.url);
      handleTaskComplete(task);
    } else {
      window.open(task.url, "_blank", "noopener,noreferrer");
      handleTaskComplete(task);
    }
  };

  const handleVoiceTaskComplete = (blob) => {
    setRecordedVoice(blob);
    console.log("Voice task blob saved in state:", blob);
  };

  const todayLocal = getLocalDayKey();
  const isToday = localStorage.getItem("tasksDate") === todayLocal;
  const completedCount = isToday ? Math.min(Object.keys(taskIdsObj).length, DAILY_TASKS) : 0;
  const progressPercent = isToday ? Math.min((completedCount / DAILY_TASKS) * 100, 100) : 0;
  const pointsToShow = isToday ? Math.min(completedCount * TASK_POINTS, MAX_POINTS) : 0;
  const allDone = completedCount >= DAILY_TASKS;

  return (
    <div style={{
      minHeight: "100vh", width: "100%",
     color: T.textPrimary,
      fontFamily: "system-ui, -apple-system, sans-serif",
      overflowX: "hidden", paddingBottom: 96,
      position: "relative",
    }}>
      <FloatDots />

      {/* gold scroll bar */}
      <div style={{
        position: "fixed", top: 0, left: 0, right: 0, height: 3, zIndex: 50,
        background: "rgba(255,255,255,0.04)",
      }}>
        <motion.div
          animate={{ width: `${progressPercent}%` }}
          transition={{ duration: 0.6 }}
          style={{
            height: "100%",
            background: `linear-gradient(90deg,${T.accent},${T.blue})`,
          }}
        />
      </div>

      <div style={{
        position: "relative", zIndex: 10,
        maxWidth: 1100, margin: "0 auto",
        padding: "0 clamp(16px, 4vw, 24px)",
      }}>

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          style={{ paddingTop: 48, marginBottom: 36 }}
        >
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "5px 14px",
            background: T.accentDim,
            border: `1px solid rgba(234,179,8,0.25)`,
            borderRadius: 999,
            color: T.accent, fontSize: 9, fontWeight: 900,
            letterSpacing: "0.28em", textTransform: "uppercase",
            marginBottom: 14,
          }}>
            <motion.span
              animate={{ opacity: [1, 0.2, 1] }}
              transition={{ duration: 1.4, repeat: Infinity }}
              style={{ width: 5, height: 5, borderRadius: "50%", background: T.accent, display: "inline-block" }}
            />
            Daily Operations
          </div>

          <h1 style={{
            fontSize: "clamp(1.8rem, 6vw, 2.8rem)",
            fontWeight: 900, letterSpacing: "-0.04em",
            textTransform: "uppercase", margin: "0 0 6px",
          }}>
            Active <span style={{ color: T.accent }}>Operations</span>
          </h1>
          <p style={{ fontSize: 10, fontWeight: 900, color: T.textMuted, letterSpacing: "0.3em", textTransform: "uppercase" }}>
            Daily Yield Tasks
          </p>
        </motion.div>

        {/* ── Progress Card ── */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          style={{
            background: T.bgCard,
            border: `1px solid ${allDone ? "rgba(34,197,94,0.35)" : "rgba(234,179,8,0.22)"}`,
            borderRadius: 28,
            padding: "clamp(20px, 4vw, 28px) clamp(16px, 4vw, 28px) clamp(20px, 4vw, 24px)",
            marginBottom: 28,
            position: "relative", overflow: "hidden",
            boxShadow: allDone
              ? "0 0 40px rgba(34,197,94,0.1), 0 20px 60px rgba(0,0,0,0.45)"
              : `0 0 40px rgba(234,179,8,0.08), 0 20px 60px rgba(0,0,0,0.45)`,
          }}
        >
          {/* top accent line */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 3,
            background: allDone
              ? `linear-gradient(90deg,${T.success},transparent)`
              : `linear-gradient(90deg,${T.accent},${T.blue},transparent)`,
          }} />

          {/* shimmer */}
          <motion.div
            initial={{ x: "-120%" }}
            animate={{ x: "120%" }}
            transition={{ delay: 0.8, duration: 1.1, ease: "easeInOut", repeat: Infinity, repeatDelay: 5 }}
            style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(105deg,transparent 30%,rgba(255,255,255,0.03) 50%,transparent 70%)",
              pointerEvents: "none",
            }}
          />

          {/* decorative chip */}
          <motion.div
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            style={{ position: "absolute", right: 20, top: 20, opacity: 0.04 }}
          >
            <FaMicrochip size={100} color={T.accent} />
          </motion.div>

          {/* top row */}
          <div style={{
            display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-end", gap: 16,
            marginBottom: 22, position: "relative", zIndex: 1,
          }}>
            <div>
              <div style={{ fontSize: 9, fontWeight: 900, color: T.accent, letterSpacing: "0.22em", textTransform: "uppercase", marginBottom: 6 }}>
                Current Efficiency
              </div>
              <motion.div
                key={progressPercent}
                initial={{ scale: 1.1 }}
                animate={{ scale: 1 }}
                style={{ fontSize: "clamp(2.2rem, 8vw, 3rem)", fontWeight: 900, color: T.textPrimary, letterSpacing: "-0.05em", lineHeight: 1 }}
              >
                {Math.round(progressPercent)}
                <span style={{ fontSize: "clamp(1rem, 4vw, 1.2rem)", color: T.textMuted }}>%</span>
              </motion.div>
            </div>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: 9, fontWeight: 900, color: T.textMuted, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 6 }}>
                Accrued Points
              </div>
              <div style={{ fontSize: "clamp(1.2rem, 4vw, 1.4rem)", fontWeight: 900, color: T.textPrimary }}>
                <span style={{ color: T.accent }}>{pointsToShow}</span>
                <span style={{ color: T.textMuted, fontSize: "clamp(0.8rem, 3vw, 0.9rem)" }}> / {MAX_POINTS}</span>
              </div>
            </div>
          </div>

          {/* progress bar */}
          <div style={{
            width: "100%", height: 10,
            background: "rgba(255,255,255,0.05)",
            borderRadius: 999, overflow: "hidden",
            marginBottom: 20, position: "relative", zIndex: 1,
          }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              style={{
                height: "100%", borderRadius: 999,
                background: allDone
                  ? `linear-gradient(90deg,${T.success},#16a34a)`
                  : `linear-gradient(90deg,${T.accent},#f59e0b)`,
                boxShadow: allDone
                  ? `0 0 16px rgba(34,197,94,0.5)`
                  : `0 0 16px rgba(234,179,8,0.5)`,
              }}
            />
          </div>

          {/* stat chips row */}
          <div style={{
            display: "flex", gap: 12, flexWrap: "wrap",
            position: "relative", zIndex: 1,
          }}>
            <StatChip
              icon={allDone ? <FaCheckCircle /> : <FaClock />}
              label={allDone ? "Status" : "Remaining"}
              value={allDone ? "Complete!" : `${DAILY_TASKS - completedCount} tasks`}
              color={allDone ? T.success : T.accent}
            />
            <StatChip
              icon={<FaTrophy />}
              label="Progress"
              value={`${completedCount} / ${DAILY_TASKS}`}
              color={T.blue}
            />
            <StatChip
              icon={<FaBolt />}
              label="Per Task"
              value={`+${TASK_POINTS} pts`}
              color={T.accent}
            />
          </div>

          {/* all done banner */}
          <AnimatePresence>
            {allDone && (
              <motion.div
                initial={{ opacity: 0, y: 16, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ type: "spring", stiffness: 280, damping: 20 }}
                style={{
                  marginTop: 18,
                  padding: "14px 20px",
                  background: T.successDim,
                  border: `1px solid rgba(34,197,94,0.3)`,
                  borderRadius: 16,
                  display: "flex", alignItems: "center", gap: 12,
                  position: "relative", zIndex: 1,
                }}
              >
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  style={{ color: T.success, fontSize: 18, flexShrink: 0 }}
                >
                  <FaFire />
                </motion.div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 900, color: T.success, letterSpacing: "0.05em" }}>
                    All Operations Complete!
                  </div>
                  <div style={{ fontSize: 10, color: "rgba(34,197,94,0.7)", marginTop: 2 }}>
                    Daily points synced to your account.
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* ── Task Cards Grid ── */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
          gap: 20,
        }}>
          {dailyTasks.map((task, i) => {
            const fromX = i % 2 === 0 ? -50 : 50;
            return (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, x: fromX, scale: 0.94 }}
                whileInView={{ opacity: 1, x: 0, scale: 1 }}
                viewport={{ once: true, amount: 0.12 }}
                transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1], delay: 0.08 * i }}
              >
                {/* shimmer wrapper on task cards */}
                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ type: "spring", stiffness: 300, damping: 22 }}
                  style={{
                    background: T.bgCard,
                    border: `1px solid ${task.status === "completed" ? "rgba(34,197,94,0.3)" : T.border}`,
                    borderRadius: 22,
                    overflow: "hidden",
                    position: "relative",
                    boxShadow: task.status === "completed"
                      ? "0 0 24px rgba(34,197,94,0.1), 0 12px 40px rgba(0,0,0,0.4)"
                      : "0 12px 40px rgba(0,0,0,0.35)",
                    transition: "border-color 0.25s",
                  }}
                  onMouseEnter={e => {
                    if (task.status !== "completed")
                      e.currentTarget.style.borderColor = "rgba(234,179,8,0.35)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = task.status === "completed"
                      ? "rgba(34,197,94,0.3)" : T.border;
                  }}
                >
                  {/* top accent per task */}
                  <div style={{
                    position: "absolute", top: 0, left: 0, right: 0, height: 2,
                    background: task.status === "completed"
                      ? `linear-gradient(90deg,${T.success},transparent)`
                      : `linear-gradient(90deg,${T.accent},transparent)`,
                  }} />

                  {/* mount shimmer */}
                  <motion.div
                    initial={{ x: "-120%" }}
                    whileInView={{ x: "120%" }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.1, duration: 0.8, ease: "easeInOut" }}
                    style={{
                      position: "absolute", inset: 0,
                      background: "linear-gradient(105deg,transparent 30%,rgba(255,255,255,0.03) 50%,transparent 70%)",
                      pointerEvents: "none", zIndex: 1,
                    }}
                  />

                  {/* task number badge */}
                  <div style={{
                    position: "absolute", top: 12, right: 14,
                    fontSize: 9, fontWeight: 900,
                    color: task.status === "completed" ? T.success : T.accent,
                    background: task.status === "completed" ? T.successDim : T.accentDim,
                    border: `1px solid ${task.status === "completed" ? "rgba(34,197,94,0.2)" : "rgba(234,179,8,0.2)"}`,
                    padding: "3px 9px", borderRadius: 999,
                    letterSpacing: "0.15em", textTransform: "uppercase",
                    zIndex: 2,
                    display: "flex", alignItems: "center", gap: 4,
                  }}>
                    {task.status === "completed" ? <FaCheckCircle size={8} /> : <FaBolt size={8} />}
                    {task.status === "completed" ? "Done" : `+${task.reward}pts`}
                  </div>

                  {/* inject TaskCard inside styled wrapper */}
                  <div style={{ position: "relative", zIndex: 2 }}>
                    <TaskCard task={task} onStart={handleStartTask} />
                  </div>
                </motion.div>
              </motion.div>
            );
          })}
        </div>

        {/* ── Empty state ── */}
        {dailyTasks.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            style={{
              textAlign: "center",
              padding: "64px 24px",
              background: T.bgCard,
              border: `1px solid ${T.border}`,
              borderRadius: 24,
              marginTop: 24,
            }}
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2.5, repeat: Infinity }}
              style={{ fontSize: 40, marginBottom: 16 }}
            >
              ⚙️
            </motion.div>
            <div style={{ fontSize: 14, fontWeight: 900, color: T.textSecondary, textTransform: "uppercase", letterSpacing: "0.1em" }}>
              No operations available
            </div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 6 }}>
              Check back tomorrow for new daily tasks.
            </div>
          </motion.div>
        )}
      </div>

      <VideoModal videoUrl={videoUrl} onClose={() => setVideoUrl(null)} />
    </div>
  );
}